import { Action, createReducer, on } from '@ngrx/store';
import { ProviderCredential } from 'src/app/admin/admin.models';
import { resetState } from '../../../store/app.actions';
import { ListDTO } from '../../loading-state/api.models';
import { Loadable } from '../../loading-state/loadable';
import { Vehicle } from '../models/vehicle.class';
import { Fleet, VehicleBrand } from '../models/vehicle.models';
import * as VehicleActions from './vehicle.actions';

export type VehicleState = {
  loadingStatus: {
    vehicleList: boolean;
    fleetList: boolean;
  };
  /**
   * TODO: Phase this out in favour of loadable `vehicles`
   * @deprecated superseded by `vehicles` right below
   */
  vehicleList?: Vehicle[] | null;
  vehicles: Loadable<Vehicle[]>;
  brands: Loadable<ListDTO<VehicleBrand>>;
  fleets: Loadable<Fleet[]>;
  /**
   * TODO: Phase this out in favour of loadable `vehicles`
   * @deprecated superseded by `vehicles` right below
   */
  fleetList?: Fleet[] | null;
  fleetMap?: Record<number, Fleet | undefined>;
  providerCredentials: Loadable<ProviderCredential[]>;
};

const initialState: VehicleState = {
  loadingStatus: {
    vehicleList: false,
    fleetList: false,
  },
  vehicles: new Loadable(),
  brands: new Loadable(),
  fleets: new Loadable(),
  providerCredentials: new Loadable(),
};

const reducer = createReducer<VehicleState>(
  initialState,
  on(resetState, () => ({ ...initialState })),
  // -------- Individual Vehicle
  on(VehicleActions.setVehicleColor, (state, { vehicleId, newColor }) => {
    const vehicleIndex = state.vehicleList?.findIndex((v: Vehicle) => v.id === vehicleId);
    if (vehicleIndex === undefined || vehicleIndex === -1) return state;
    const vehicle = structuredClone(state.vehicleList![vehicleIndex]);
    vehicle.user_color = newColor;
    const newVehicleList = [
      ...state.vehicleList!.slice(0, vehicleIndex),
      vehicle,
      ...state.vehicleList!.slice(vehicleIndex + 1),
    ];
    return { ...state, vehicleList: newVehicleList };
  }),
  // -------- Vehicle List
  on(
    VehicleActions.getVehicleList,
    VehicleActions.createVehicleList,
    VehicleActions.deleteVehicleList,
    VehicleActions.unarchiveVehicleList,
    state => ({
      ...state,
      loadingStatus: {
        ...state.loadingStatus,
        vehicleList: true,
      },
      vehicles: state.vehicles.copy({ loading: true }),
    })
  ),
  on(VehicleActions.setVehicleList, (state, { vehicleList, noUpdate }) => ({
    ...state,
    loadingStatus: {
      ...state.loadingStatus,
      vehicleList: false,
    },
    vehicleList: noUpdate === undefined ? vehicleList : state.vehicleList,
    vehicles: noUpdate === undefined ? new Loadable(vehicleList) : state.vehicles,
  })),
  // -------- Fleet List
  on(
    VehicleActions.getFleetList,
    VehicleActions.createFleetList,
    VehicleActions.deleteFleetList,
    state => ({
      ...state,
      loadingStatus: {
        ...state.loadingStatus,
        fleetList: true,
      },
      fleets: state.fleets.copy({ loading: true }),
    })
  ),
  on(VehicleActions.setFleetList, (state, { fleetList }) => {
    const fleetMap: VehicleState['fleetMap'] = {};
    fleetList?.forEach(fleet => (fleetMap[fleet.id] = fleet));

    return {
      ...state,
      loadingStatus: {
        ...state.loadingStatus,
        fleetList: false,
      },
      fleets: new Loadable(fleetList),
      fleetList,
      fleetMap,
    };
  }),
  // -------- Vehicle Provider Credentials
  on(VehicleActions.getProviderCredentials, VehicleActions.createProviderCredentials, state => ({
    ...state,
    providerCredentials: state.providerCredentials.loadingCopy(true),
  })),
  on(VehicleActions.setProviderCredentials, (state, { providerCredentials, error }) => ({
    ...state,
    providerCredentials:
      error === undefined
        ? new Loadable(providerCredentials)
        : state.providerCredentials.errorCopy(error),
  })),
  // -------- Vehicle Brands
  on(VehicleActions.getVehicleBrands, state => ({
    ...state,
    brands: state.brands.copy({ loading: true }),
  })),
  on(VehicleActions.setVehicleBrands, (state, { brands }) => ({
    ...state,
    brands: new Loadable(brands),
  }))
);

export function vehicleReducer(state: VehicleState | undefined, action: Action): VehicleState {
  return reducer(state, action);
}
export const vehicleFeatureKey = 'vehicle';
